/**
 * A customer's CRM status (CRM-API-1, api#1038): `customers.status`, one of
 * prospect | active | inactive (fieldiq-api src/lib/customer-status.ts).
 *
 * THE MAP IS EXHAUSTIVE AND AN UNKNOWN VALUE CLAIMS NOTHING. A value outside
 * the enum (a new status the API grows before this file does, or a row read
 * before the column existed) renders a slate badge reading "—" and a
 * transparent rail — never a real status. `UNKNOWN_STATUS` in
 * EquipmentScreen.tsx is the pattern; on a customer register the guess that
 * matters is "active", which would tell the office a prospect is a client.
 *
 * Hues are the existing signal hues, grouped by meaning, no new colour:
 *   prospect → blue   (not yet a client; someone is working it)
 *   active   → green  (a client)
 *   inactive → slate  (a client on file who is not currently working with us)
 */
export const CUSTOMER_STATUSES = ['prospect', 'active', 'inactive'] as const;
export type CustomerStatus = (typeof CUSTOMER_STATUSES)[number];

export const CUSTOMER_STATUS_LABELS: Record<CustomerStatus, string> = {
  prospect: 'Prospect',
  active: 'Active',
  inactive: 'Inactive',
};

export interface CustomerStatusSkin {
  label: string;
  fill: string;
  rail: string;
}

export const CUSTOMER_STATUS_SKIN: Record<CustomerStatus, CustomerStatusSkin> = {
  prospect: { label: CUSTOMER_STATUS_LABELS.prospect, fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  active: { label: CUSTOMER_STATUS_LABELS.active, fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  inactive: { label: CUSTOMER_STATUS_LABELS.inactive, fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};

export const UNKNOWN_CUSTOMER_STATUS: CustomerStatusSkin = {
  label: '—',
  fill: 'bg-fi-signal-slate',
  rail: 'border-l-transparent',
};

export function isCustomerStatus(value: unknown): value is CustomerStatus {
  return typeof value === 'string' && (CUSTOMER_STATUSES as readonly string[]).includes(value);
}

export function customerStatusSkin(status: unknown): CustomerStatusSkin {
  return isCustomerStatus(status) ? CUSTOMER_STATUS_SKIN[status] : UNKNOWN_CUSTOMER_STATUS;
}

/** The status a customer row carries, tolerating both casings; `null` when absent. */
export function readCustomerStatus(row: any): string | null {
  return row?.status ?? null;
}
